import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { imageSrc } from "../../lib/api";
import { tracks } from "../../lib/tracks";
import { audio } from "../../lib/audio";
import { immersionChoice, logMode, mark, perf, reducedMotion, times } from "../../lib/perf";
import { GLBoundary } from "../GLBoundary";
import { arcEnd, arcFor, calmPlan, revealAt, stageAt } from "./arcs";
import { CTA_CLASS, JourneyCTA } from "./JourneyCTA";
import { StrokeReveal } from "./StrokeReveal";
import { ArcCaptions, ArcEye, ArcLids, ArcLine, ArcMoments, ArcNote, ArcSunNames, ArcWords, CalmArc, calmClock } from "./StoryArc";

const ImmersionScene = lazy(() => import("./ImmersionScene"));
const loadArcScene = () => import("./ArcScene");
const ArcScene = lazy(loadArcScene);
// a painting's projection image, journey textures, music and cues, fetched ahead (also for the next painting while one plays or its details are open)
export function prefetchJourney(painting, full = true, light = false) {
  const arc = painting && arcFor(painting);
  if (!arc) return;
  const music = [arc.music, arc.music2?.[1], arc.calmMusic, arc.approach?.music].filter(Boolean).map((m) => imageSrc(`/music/${m}.mp3`));
  // light: a low-priority fetch into the HTTP cache only (nothing decoded or uploaded on the main thread)
  if (light) {
    [imageSrc(painting.image_url), ...music].forEach((u) => fetch(u, { priority: "low" }).catch(() => {}));
    return;
  }
  new Image().src = imageSrc(painting.image_url);
  music.forEach((m) => tracks.preload?.(m));
  if (arc.sounds) audio.preloadCues(arc.sounds);
  if (full) loadArcScene().then((m) => m.preloadArc(arc, painting)).catch(() => {});
}

function SimpleImmersion({ painting, onReady }) {
  return (
    <div data-testid="immersion-simple" className="absolute inset-0 overflow-hidden">
      <motion.img
        src={imageSrc(painting.image_url)}
        alt={painting.title}
        onLoad={onReady}
        onError={onReady}
        initial={{ scale: 1.02, x: "0%", y: "0%" }}
        animate={{ scale: 1.2, x: "-3%", y: "-2%" }}
        transition={{ duration: 16, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" }}
        style={{ transformOrigin: "50% 45%" }}
        className="absolute inset-0 h-full w-full object-cover"
      />
    </div>
  );
}

// While the room is still opening: a single brushstroke painting itself in.
function BrushLoader() {
  return (
    <motion.div data-testid="projection-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3">
      <svg width="220" height="60" viewBox="0 0 220 60" aria-hidden="true">
        <path className="brush-draw" d="M12 38 C 50 14, 88 50, 120 30 S 190 18, 208 30" fill="none" stroke="#9B7BC4" strokeWidth="9" strokeLinecap="round" />
      </svg>
      <p className="font-display text-base italic text-[#E9DCF5]/80">the painting is opening…</p>
    </motion.div>
  );
}

// ?debug=1: the chosen mode and why, on screen (for checking a visitor's own device).
function ModeDebug() {
  const [, set] = useState(0);
  useEffect(() => {
    const on = () => set((n) => n + 1);
    window.addEventListener("afae-mode", on);
    const id = setInterval(on, 1000);
    return () => (window.removeEventListener("afae-mode", on), clearInterval(id));
  }, []);
  const L = window.__afaeModeLog || {};
  return (
    <pre data-testid="mode-debug" className="pointer-events-none fixed right-3 top-20 z-[95] max-w-[70vw] whitespace-pre-wrap rounded-lg bg-black/75 p-2 font-mono text-[10px] leading-tight text-[#FFF4DC]">
      {Object.entries(L).map(([k, v]) => `${k}: ${v.mode} (${v.reason})`).join("\n")}
      {`\ndpr ${window.__afaeDpr?.dpr ?? "-"} fps ${window.__afaeDpr?.fps ?? "-"} cores ${navigator.hardwareConcurrency ?? "?"} mem ${navigator.deviceMemory ?? "?"}`}
    </pre>
  );
}

export default function Immersion({ painting, onDone, onNext, nextLabel }) {
  const arc = arcFor(painting);
  const [mode, setMode] = useState(() => immersionChoice().mode);
  const [glFail, setGlFail] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shown, setShown] = useState(true);
  const [hint, setHint] = useState(null);
  const [tilt, setTilt] = useState(null);
  const [stageId, setStageId] = useState(null);
  // the journey's scene fades in over the still-running projection, so the screen never drops to black
  const [arcShown, setArcShown] = useState(false);
  // the fade-in class is dropped once it ends, so no animated layer lingers over the WebGL canvases
  const [faded, setFaded] = useState(false);
  const [projGone, setProjGone] = useState(false);
  if (projGone) window.__afaeArcAlone = true;
  useEffect(() => {
    if (!arcShown) return;
    const id = setTimeout(() => setProjGone(true), 1100);
    return () => (clearTimeout(id), delete window.__afaeArcAlone);
  }, [arcShown]);
  const clock = useRef(null);
  const revealed = useRef(false);
  // the projection may fall back to the plain image on a slow start, but the journey only goes calm for a real reason
  const [projWhy, setProjWhy] = useState(null);
  const calm = playing && (reducedMotion() || immersionChoice().mode !== "full" || !!glFail);
  const calmWhy = reducedMotion() ? "prefers-reduced-motion is on" : glFail || (immersionChoice().mode !== "full" ? immersionChoice().reason : null);
  const cp = useMemo(() => (arc ? calmPlan(arc, painting.about) : null), [arc, painting.about]);
  useEffect(() => {
    logMode(playing ? "journey" : "projection", playing ? (calm ? "calm" : "full") : mode, (playing ? (calm ? calmWhy : null) : projWhy) || immersionChoice().reason);
  }, [playing, calm, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  // everything the journey needs is fetched while the projection room is open, so nothing pops in mid-scene
  useEffect(() => {
    if (!arc) return;
    prefetchJourney(painting, mode === "full" && !reducedMotion());
    if (cp?.frames) cp.scenes.forEach((sc, i) => (new Image().src = `${process.env.PUBLIC_URL || ""}${sc.src || `/arcs/calm/${arc.slug}-${i}.webp`}`));
  }, [arc]); // eslint-disable-line react-hooks/exhaustive-deps
  // a journey scene that never arrives (a failed asset, a lost context) plays its calm telling instead, never a dead screen
  useEffect(() => {
    if (!playing || calm) return;
    const id = setTimeout(() => !clock.current && setGlFail("the journey scene did not load within 25s"), 25000);
    return () => clearTimeout(id);
  }, [playing, calm]);
  useEffect(() => {
    if (!loaded) return;
    const id = setTimeout(() => setShown(false), 700);
    return () => clearTimeout(id);
  }, [loaded]);
  useEffect(() => {
    window.__afaeProjectionLoading = !loaded;
    return () => delete window.__afaeProjectionLoading;
  }, [loaded]);

  // The projection room offers its buttons after 3s; a story arc earns "See the painting" at its own end.
  useEffect(() => {
    setReady(false);
    if (!playing) {
      const a = setTimeout(() => setReady(true), 3000);
      return () => clearTimeout(a);
    }
    clock.current = null;
    revealed.current = false;
    const end = arcEnd(arc, painting.about, calm);
    if (calm) calmClock.reset();
    window.__afaeCalmSkip = () => calmClock.skipTo(end);
    const id = setInterval(() => {
      const t = calm ? calmClock.now() : clock.current?.t ?? 0;
      if (calm) setHint(cp.scenes.length ? cp.hintAt(t) : "breathe in");
      else setRevealing(!!window.__afaeReveal?.started);
      if (!calm && clock.current) {
        const s = stageAt(arc, t);
        setHint(s.hint);
        setStageId(arc.tilt && t >= arc.tilt.from && t < arc.tilt.until ? "tilt" : s.id);
      }
      if (t >= end || revealed.current) setReady(true);
    }, 300);
    return () => clearInterval(id);
  }, [arc, playing, calm, painting.about]); // eslint-disable-line react-hooks/exhaustive-deps
  // Some arcs flow straight on into the painting's own special experience.
  useEffect(() => {
    if (!(ready && playing && arc?.flowInto)) return;
    const id = setTimeout(onDone, 1400);
    return () => clearTimeout(id);
  }, [ready, playing, arc, onDone]);
  // A projection texture that never arrives falls back to the plain image; the room always opens.
  useEffect(() => {
    if (mode !== "full" || loaded || playing) return;
    const id = setTimeout(() => (setProjWhy("the projection texture took over 20s"), setMode("simple")), 20000);
    return () => clearTimeout(id);
  }, [mode, loaded, playing]);
  useEffect(() => {
    window.__afaeImmersionMode = playing ? (calm ? "arc-calm" : "arc") : mode;
  }, [mode, playing, calm]);
  const play = () => {
    mark("playTap", ["arcShown"]);
    setHint(arc.stages[0].hint);
    setPlaying(true);
    const calmNow = reducedMotion() || immersionChoice().mode !== "full" || !!glFail;
    const m = calmNow ? arc.calmMusic || arc.music : arc.musicAt == null && arc.music;
    if (m) tracks.play(imageSrc(`/music/${m}.mp3`));
    else if (arc.quiet) tracks.play(null, false, 0.35);
  };
  // Tilt plays the paint only once the visitor grants motion access (iOS asks); drag always works.
  const askTilt = async () => {
    try {
      const r = await window.DeviceOrientationEvent?.requestPermission?.();
      setTilt(r === undefined || r === "granted" ? "on" : "denied");
    } catch {
      setTilt("denied");
    }
  };

  return (
    <motion.div
      data-testid="immersion-experience"
      data-mode={mode}
      data-arc={playing ? (calm ? "calm" : "full") : "off"}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.9 }}
      onContextMenu={(e) => e.preventDefault()}
      onAnimationEnd={(e) => e.target === e.currentTarget && setFaded(true)}
      className={`${faded ? "" : "afae-fadein "}no-callout fixed inset-0 z-50 overflow-clip bg-[#05060C]`}
    >
      {playing && !calm && projGone ? null : playing && calm ? (
        <CalmArc painting={painting} arc={arc} plan={cp} onReady={() => times.arcShown || mark("arcShown")} />
      ) : mode === "full" ? (
        <GLBoundary onError={() => (setProjWhy("the projection's WebGL failed"), setGlFail("the projection's WebGL failed"), setMode("simple"))}>
          <Suspense fallback={null}>
            <ImmersionScene painting={painting} onLowFps={() => (setProjWhy("the projection ran under 8fps for 6s"), setMode("simple"))} onReady={() => setLoaded(true)} />
          </Suspense>
        </GLBoundary>
      ) : (
        <SimpleImmersion painting={painting} onReady={() => setLoaded(true)} />
      )}
      {playing && !calm && (
        <motion.div className="absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: arcShown ? 1 : 0 }} transition={{ duration: 0.9 }}>
          <GLBoundary key="arc" onError={() => setGlFail("the journey's WebGL scene failed")}>
            <Suspense fallback={null}>
              <ArcScene painting={painting} arc={arc} clock={clock} onFail={setGlFail} onReady={() => requestAnimationFrame(() => requestAnimationFrame(() => (times.arcShown || mark("arcShown"), setArcShown(true))))} />
            </Suspense>
          </GLBoundary>
        </motion.div>
      )}
      {playing && !calm && <StrokeReveal src={imageSrc(painting.image_url)} cfg={arc.reveal} clock={clock} at={revealAt(arc)} onDone={() => (revealed.current = true)} />}
      {playing && !calm && arc.words && <ArcWords arc={arc} clock={clock} />}
      {playing && !calm && arc.eye && <ArcEye arc={arc} clock={clock} />}
      {playing && !calm && arc.lids && <ArcLids arc={arc} clock={clock} />}
      {playing && !calm && arc.plants && <ArcNote />}
      {playing && !calm && arc.names && <ArcSunNames arc={arc} clock={clock} />}
      {playing && !calm && arc.moments && <ArcMoments arc={arc} clock={clock} />}
      {playing && <ArcLine key={calm ? "c" : "f"} arc={arc} calm={calm} clock={clock} span={calm ? cp.line : null} />}
      {playing && <ArcCaptions key={calm ? "cc" : "fc"} arc={arc} about={painting.about} calm={calm} clock={clock} />}
      {playing && !calm && stageId === "tilt" && tilt !== "on" && tilt !== "denied" && "DeviceOrientationEvent" in window && window.matchMedia?.("(pointer: coarse)").matches && (
        <button data-testid="arc-tilt-button" onClick={askTilt} className="absolute bottom-36 left-1/2 -translate-x-1/2 rounded-full border border-white/60 bg-[#2D1E3E]/55 px-5 py-1.5 font-display text-base italic text-white backdrop-blur-md">
          tilt the canvas too
        </button>
      )}
      <AnimatePresence>{shown && !playing && <BrushLoader key="loader" />}</AnimatePresence>
      <div className={`pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_58%,rgba(5,6,16,0.6)_100%)]`} />
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 2, delay: 1.2 }}
        style={{ x: "-50%" }}
        className={`pointer-events-none absolute left-1/2 top-[7.5rem] w-max max-w-[80vw] rounded-full bg-[#22152F]/40 px-4 py-1 text-center backdrop-blur-sm transition-opacity duration-700 sm:top-5 ${revealing ? "opacity-0" : "opacity-100"}`}
      >
        <p data-testid="immersion-emotion" className="font-display text-sm italic tracking-wide text-[#FFF4DC]/85 sm:text-base">{painting.title}</p>
      </motion.div>
      <AnimatePresence mode="wait">
        {!revealing && (playing ? hint : "breathe in") && (
          <motion.p
            key={playing ? hint : "breathe"}
            data-testid="immersion-hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.75, 0.75, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 5, times: [0, 0.15, 0.75, 1] }}
            className="pointer-events-none absolute inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+6.5rem)] text-center font-display text-xs italic tracking-wider text-[#F6E7C8] sm:text-sm"
          >
            {playing ? hint : "breathe in"}
          </motion.p>
        )}
      </AnimatePresence>
      <JourneyCTA show={ready}>
        {/* a story painting's details open only after its journey */}
        {(!arc || (playing && ready)) && (
          <button data-testid="immersion-continue-button" onClick={onDone} disabled={!ready} className={CTA_CLASS}>
            {playing && arc.endLabel ? arc.endLabel : "See the painting ✦"}
          </button>
        )}
        {arc && playing && ready && onNext && !arc.flowInto && (
          <button data-testid="immersion-next-button" onClick={onNext} className="rounded-full px-5 py-2 font-display text-lg italic text-[#FFF4DC]/85 transition-colors duration-200 hover:text-[#FFF4DC] [touch-action:manipulation]">
            {nextLabel || "Next painting ›"}
          </button>
        )}
        {arc && !playing && (
          <button data-testid="immersion-experience-button" onClick={play} disabled={!ready} className={CTA_CLASS}>
            Experience the painting ✦
          </button>
        )}
      </JourneyCTA>
      {perf.debug && <ModeDebug />}
    </motion.div>
  );
}
